import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Save, Plus, Trash2, Edit } from 'lucide-react';

const AdminMenus = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMenu, setEditingMenu] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}`, 'Content-Type': 'application/json' };
  };

  const fetchMenus = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/menus`, { headers });
      if (!response.ok) throw new Error('Unauthorized');
      const data = await response.json();
      setMenus(data);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les menus", variant: "destructive" });
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, []);

  const handleEdit = (menu) => {
    setEditingMenu(menu.id);
    setEditForm({ ...menu });
  };

  const handleSave = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/menus/${editingMenu}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Menu mis à jour" });
        setEditingMenu(null);
        setEditForm(null);
        fetchMenus();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder", variant: "destructive" });
    }
  };

  const handleCancel = () => {
    setEditingMenu(null);
    setEditForm(null);
  };

  const updateWeeklyMenu = (field, value) => {
    setEditForm({
      ...editForm,
      weekly_menu: {
        ...editForm.weekly_menu,
        [field]: value
      }
    });
  };

  const updateDish = (section, index, field, value) => {
    const newSections = { ...editForm.sections };
    newSections[section].dishes[index][field] = value;
    setEditForm({ ...editForm, sections: newSections });
  };

  const addDish = (section) => {
    const newSections = { ...editForm.sections };
    newSections[section].dishes.push({ name: '', description: '', price: '' });
    setEditForm({ ...editForm, sections: newSections });
  };

  const removeDish = (section, index) => {
    const newSections = { ...editForm.sections };
    newSections[section].dishes.splice(index, 1);
    setEditForm({ ...editForm, sections: newSections });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <h1 className="text-xl font-bold text-gray-900">Gestion des Menus</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {menus.map((menu) => (
          <div key={menu.id} className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{menu.title}</h2>
                <p className="text-gray-500">{menu.menu_type === 'midi' ? 'Menu Déjeuner' : 'Carte du Soir'}</p>
              </div>
              {editingMenu !== menu.id && (
                <Button onClick={() => handleEdit(menu)} className="flex items-center gap-2">
                  <Edit size={18} />
                  Modifier
                </Button>
              )}
            </div>

            {editingMenu === menu.id ? (
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Titre</Label>
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Sous-titre</Label>
                    <Input
                      value={editForm.subtitle}
                      onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Weekly Menu (for midi) */}
                {menu.menu_type === 'midi' && editForm.weekly_menu && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h3 className="font-bold text-lg mb-4">Menu de la Semaine</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>Prix</Label>
                        <Input
                          value={editForm.weekly_menu.price}
                          onChange={(e) => updateWeeklyMenu('price', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Dates</Label>
                        <Input
                          value={editForm.weekly_menu.dates}
                          onChange={(e) => updateWeeklyMenu('dates', e.target.value)}
                          placeholder="Du mardi X au vendredi Y"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label>Entrée</Label>
                        <Input
                          value={editForm.weekly_menu.entree}
                          onChange={(e) => updateWeeklyMenu('entree', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Plat</Label>
                        <Input
                          value={editForm.weekly_menu.plat}
                          onChange={(e) => updateWeeklyMenu('plat', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Dessert</Label>
                        <Input
                          value={editForm.weekly_menu.dessert}
                          onChange={(e) => updateWeeklyMenu('dessert', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Sections (for soir or additional items) */}
                {editForm.sections && (
                  <>
                    {['entrees', 'plats', 'desserts'].map((section) => (
                      editForm.sections[section]?.dishes?.length > 0 && (
                        <div key={section} className="border rounded-lg p-4">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-lg">{editForm.sections[section].title}</h3>
                            <Button variant="outline" size="sm" onClick={() => addDish(section)}>
                              <Plus size={16} className="mr-1" /> Ajouter
                            </Button>
                          </div>
                          <div className="space-y-4">
                            {editForm.sections[section].dishes.map((dish, index) => (
                              <div key={index} className="grid grid-cols-12 gap-2 items-start bg-gray-50 p-3 rounded">
                                <div className="col-span-5">
                                  <Input
                                    value={dish.name}
                                    onChange={(e) => updateDish(section, index, 'name', e.target.value)}
                                    placeholder="Nom du plat"
                                  />
                                </div>
                                <div className="col-span-4">
                                  <Input
                                    value={dish.description}
                                    onChange={(e) => updateDish(section, index, 'description', e.target.value)}
                                    placeholder="Description"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Input
                                    value={dish.price}
                                    onChange={(e) => updateDish(section, index, 'price', e.target.value)}
                                    placeholder="Prix"
                                  />
                                </div>
                                <div className="col-span-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeDish(section, index)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={18} />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                  </>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4 border-t">
                  <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700 flex items-center gap-2">
                    <Save size={18} />
                    Enregistrer
                  </Button>
                  <Button variant="outline" onClick={handleCancel}>
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-6">
                {/* Preview */}
                {menu.menu_type === 'midi' && menu.weekly_menu && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold mb-2">Menu de la Semaine - {menu.weekly_menu.price}</h3>
                    <p className="text-sm text-gray-600 mb-2">{menu.weekly_menu.dates}</p>
                    <ul className="text-sm space-y-1">
                      <li><strong>Entrée:</strong> {menu.weekly_menu.entree}</li>
                      <li><strong>Plat:</strong> {menu.weekly_menu.plat}</li>
                      <li><strong>Dessert:</strong> {menu.weekly_menu.dessert}</li>
                    </ul>
                  </div>
                )}
                
                {menu.sections && ['entrees', 'plats', 'desserts'].map((section) => (
                  menu.sections[section]?.dishes?.length > 0 && (
                    <div key={section} className="mb-4">
                      <h3 className="font-semibold mb-2">{menu.sections[section].title}</h3>
                      <ul className="text-sm space-y-1 text-gray-600">
                        {menu.sections[section].dishes.slice(0, 3).map((dish, i) => (
                          <li key={i}>{dish.name} - {dish.price}</li>
                        ))}
                        {menu.sections[section].dishes.length > 3 && (
                          <li className="text-gray-400">+ {menu.sections[section].dishes.length - 3} autres plats</li>
                        )}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        ))}
      </main>
    </div>
  );
};

export default AdminMenus;
